import axios from 'axios';
import { firestore, getFullHttpUrl } from '../firebase.config';
import authHeaders from './authHeaders';

// Shareable team invite links (?join-team=<name>&token=<token>). The token is
// issued by the team-createInviteLink function and expires; joining happens on
// the server, never by writing the team on the user's profile.

// Team name and validity of a link, for the create-account form. Null when
// the link doesn't exist or has expired.
export const getInviteLink = async (token) => {
  if (!token) {
    return null;
  }
  const snap = await firestore.collection('teamInviteLinks').doc(token).get().catch(() => null);
  if (!snap || !snap.exists || snap.data().expiresAt < Date.now()) {
    return null;
  }
  return { ...snap.data(), token };
};

// Home, sign-in and create-account can all react to the same link right
// after signing in; they share one request.
const pending = {};

// Joins the signed-in user to the link's team. Resolves to "joined",
// "already-member", "already-in-team" or "link-expired".
export const joinTeamWithLink = (token) => {
  if (!pending[token]) {
    pending[token] = (async () => {
      const res = await axios.post(getFullHttpUrl('team-joinTeamWithLink'), { token }, { headers: await authHeaders() });
      return res.data.status;
    })().finally(() => setTimeout(() => { delete pending[token]; }, 5000));
  }
  return pending[token];
};

// The create-account form holds a link while it makes the account of someone
// who arrived with it, and joins the team itself once the new profile is
// saved. Home, which joins people who are signed in and arrive with a link,
// waits meanwhile: the new profile reaches Home before the server has it
// (and the app redraws Home as the account signs in, so this can't be Home's
// state); joining then made the server write the team into a profile that
// didn't exist yet, the form's save of the profile was refused and the new
// account was left without a name, email or role ("An unexpected error
// occurred").
const held = new Set();
export const holdJoin = (token) => { if (token) held.add(token); };
export const releaseJoin = (token) => { held.delete(token); };
export const joinHeld = (token) => held.has(token);

// Where to send the person after trying to join; null means the link didn't
// work and the caller should say so. Joined: the order list, like any sign-in
// (announceJoin says it worked). Already in another team: the team page,
// which asks them to leave that team or sign out.
export const joinResultPath = (status) => {
  if (status === 'joined' || status === 'already-member') {
    return '/account/orders';
  }
  if (status === 'already-in-team') {
    return '/account/team-members?action=already-joined';
  }
  return null;
};

export const EXPIRED_LINK_MESSAGE = "This team invite link has expired or isn't valid anymore. Please ask your team admin for a new one.";

// Tells the person how the link went: joined, or that it didn't work.
// ("already-in-team" is explained by the team page it leads to.) The sign-in
// form and Home can both handle the same link at once (they share the
// request above); it is said once.
let lastAnnounced = { status: null, at: 0 };
export const announceJoin = (status, showNotification) => {
  const now = Date.now();
  const kind = status === 'already-member' ? 'joined' : status;
  if (lastAnnounced.status === kind && now - lastAnnounced.at < 5000) {
    return;
  }
  lastAnnounced = { status: kind, at: now };
  if (status === 'joined' || status === 'already-member') {
    showNotification("Team Joined", "You've successfully joined the team. You can now start using the team's features.", "success");
  } else if (!joinResultPath(status)) {
    showNotification("Invite link expired", EXPIRED_LINK_MESSAGE, "error");
  }
};
